if (!customElements.get('rudraksha-product-grid')) {
  customElements.define(
    'rudraksha-product-grid',
    class RudrakshaProductGrid extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.filterButtons = [...this.querySelectorAll('[data-product-filter]')];
        this.cards = [...this.querySelectorAll('[data-product-card]')];
        this.resultCount = this.querySelector('[data-result-count]');
        this.emptyState = this.querySelector('[data-filter-empty]');

        this.setupCollectionDescription();
        this.syncFilterAvailability();

        this.filterButtons.forEach((button) => {
          button.addEventListener('click', () => this.applyFilter(button.dataset.productFilter, button));
        });

        // Delegate submissions from the section root so forms replaced by
        // Shopify's AJAX filtering/sorting continue to use the cart drawer.
        this.onProductFormSubmit = (event) => {
          const form = event.target.closest('[data-rpg-product-form]');
          if (!form || !this.contains(form)) return;
          this.addToCart(event, form);
        };
        this.addEventListener('submit', this.onProductFormSubmit);
      }

      setupCollectionDescription() {
        const description = this.querySelector('[data-rpg-description]');
        const toggle = this.querySelector('[data-rpg-description-toggle]');
        if (!description || !toggle) return;

        const moreLabel = toggle.dataset.moreLabel || 'Read more';
        const lessLabel = toggle.dataset.lessLabel || 'Read less';

        const syncAvailability = () => {
          if (description.classList.contains('is-expanded')) return;
          toggle.hidden = description.scrollHeight <= description.clientHeight + 1;
        };

        toggle.addEventListener('click', () => {
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          description.classList.toggle('is-collapsed', expanded);
          description.classList.toggle('is-expanded', !expanded);
          toggle.setAttribute('aria-expanded', String(!expanded));
          toggle.textContent = expanded ? moreLabel : lessLabel;
        });

        window.requestAnimationFrame(syncAvailability);
        if (document.fonts?.ready) document.fonts.ready.then(syncAvailability);
        if ('ResizeObserver' in window) {
          this.descriptionResizeObserver = new ResizeObserver(syncAvailability);
          this.descriptionResizeObserver.observe(description);
        }
      }

      disconnectedCallback() {
        this.descriptionResizeObserver?.disconnect();
      }

      syncFilterAvailability() {
        const categoryCounts = this.cards.reduce((counts, card) => {
          const category = card.dataset.category || 'more';
          counts[category] = (counts[category] || 0) + 1;
          return counts;
        }, {});

        this.filterButtons.forEach((button) => {
          const filter = button.dataset.productFilter;
          const count = filter === 'all' ? this.cards.length : (categoryCounts[filter] || 0);
          button.hidden = count === 0;
          button.dataset.filterCount = String(count);
        });

        const visibleButtons = this.filterButtons.filter((button) => !button.hidden);
        let activeButton = visibleButtons.find((button) => button.classList.contains('is-active'));
        if (!activeButton) {
          activeButton = visibleButtons.find((button) => button.dataset.productFilter === 'all') || visibleButtons[0];
        }

        if (activeButton) this.applyFilter(activeButton.dataset.productFilter, activeButton);
        else {
          if (this.resultCount) this.resultCount.textContent = String(this.cards.length);
          if (this.emptyState) this.emptyState.hidden = this.cards.length !== 0;
        }
      }

      applyFilter(filter, activeButton) {
        let visibleCount = 0;

        this.filterButtons.forEach((button) => {
          const isActive = button === activeButton;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', String(isActive));
        });

        this.cards.forEach((card) => {
          const isVisible = filter === 'all' || card.dataset.category === filter;
          card.hidden = !isVisible;
          if (isVisible) visibleCount += 1;
        });

        if (this.resultCount) this.resultCount.textContent = visibleCount;
        if (this.emptyState) this.emptyState.hidden = visibleCount !== 0;
      }

      async addToCart(event, form) {
        event.preventDefault();
        const button = form.querySelector('button[type="submit"]');
        const label = button?.querySelector('[data-button-label]');
        const error = form.querySelector('.rudraksha-product-grid__form-error');
        const variantInput = form.querySelector('[data-variant-id]');
        if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

        if (!variantInput?.value || !/^\d+$/.test(variantInput.value)) {
          if (error) {
            error.textContent = 'This product does not have an available variant.';
            error.hidden = false;
          }
          return;
        }

        const originalLabel = label?.textContent || '';
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('is-loading');
        if (error) {
          error.hidden = true;
          error.textContent = '';
        }

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const formData = new FormData(form);

        if (cart && typeof cart.getSectionsToRender === 'function') {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button);
        }

        try {
          const configuredUrl = window.routes?.cart_add_url || form.getAttribute('action') || '/cart/add';
          const cartAddUrl = new URL(configuredUrl, window.location.origin);
          if (!cartAddUrl.pathname.endsWith('.js')) cartAddUrl.pathname = `${cartAddUrl.pathname}.js`;

          const response = await fetch(cartAddUrl.toString(), {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          });
          const responseText = await response.text();
          let state;
          try {
            state = JSON.parse(responseText);
          } catch (_error) {
            throw new Error('Shopify returned an invalid cart response. Please try again.');
          }

          if (!response.ok || state.status) throw new Error(state.description || 'Unable to add this product.');

          if (cart && typeof cart.renderContents === 'function') {
            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            try {
              cart.renderContents(state);
              if (typeof cart.open === 'function') cart.open(button);
            } catch (drawerError) {
              console.error('The product was added, but the cart drawer could not render.', drawerError);
              if (typeof cart.open === 'function') cart.open(button);
            }
          } else {
            window.location.assign(window.routes?.cart_url || '/cart');
            return;
          }

          if (label) label.textContent = 'Added';
          this.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { product: state } }));
          window.setTimeout(() => {
            if (label) label.textContent = originalLabel;
          }, 1400);
        } catch (exception) {
          if (error) {
            error.textContent = exception.message;
            error.hidden = false;
          }
        } finally {
          button.classList.remove('is-loading');
          button.removeAttribute('aria-disabled');
        }
      }
    }
  );
}
